import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { mlSheetService } from '../../services/mlSheetService';
import { useAuth } from '../../context/AuthContext';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import Alert from '../common/Alert.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { formatCurrency } from '../../utils/formatCurrency.js';
import { formatIstDateTime } from '../../utils/localDate.js';
import { canViewMlSheet, canAppendShipmentsOnMlSheet, hideMlSheetAmounts, canDownloadMlSheetPdf } from '../../utils/mlSheetAgentAccess.js';
import { canPrintShipmentQr, downloadShipmentQrCodesBulk } from '../../utils/shipmentQrPrint.js';
import { printPdfBlob, parseBlobErrorMessage } from '../../utils/printPdfBlob.js';
import { downloadBlob } from '../../utils/downloadBlob.js';

const TABLE_LINE = 'border border-gray-200';
const TABLE_HEAD_CELL = `px-2 py-2.5 ${TABLE_LINE} font-medium text-gray-600`;
const TABLE_CELL = `px-2 py-2.5 ${TABLE_LINE} text-gray-700`;
const TABLE_CELL_COMPACT = `px-1 py-2 ${TABLE_LINE} text-gray-700`;

const dash = (value) => (value == null || value === '' ? '-' : value);

const formatBooked = (value) => {
  const formatted = formatIstDateTime(value);
  return formatted === '—' ? '-' : formatted;
};

const paymentBadgeLabel = (shipment) => {
  const group = String(shipment.payment_group || '').toLowerCase();
  const mode = String(shipment.mode_of_payment || shipment.payment_mode || '').toLowerCase();
  if (group === 'to_pay' || mode === 'to_pay') return 'ToPay';
  if (group === 'on_account' || mode === 'credit' || mode === 'on_account') return 'On Acc';
  if (group || mode) return 'Paid';
  return null;
};

const canEditMlSheet = (sheet, user) => {
  if (sheet?.is_editable != null) return Boolean(sheet.is_editable);
  return ['System Admin', 'Admin', 'Accountant'].includes(user?.role);
};

const MlSheetDetailModal = ({ mlSheetId, onClose, onOpenShipment }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sheet, setSheet] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [qrBusy, setQrBusy] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsMenuRef = useRef(null);

  useEffect(() => {
    if (!mlSheetId) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mlSheetId]);

  useEffect(() => {
    if (!mlSheetId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const response = await mlSheetService.getMLSheet(mlSheetId);
        if (cancelled) return;
        const sheetData = response.data?.ml_sheet || response.data?.data || null;
        const shipmentData = response.data?.shipments || [];
        setSheet({
          ...sheetData,
          hide_amounts: Boolean(response.data?.hide_amounts),
          can_download_pdf: response.data?.can_download_pdf,
        });
        setShipments(Array.isArray(shipmentData) ? shipmentData : []);
      } catch (error) {
        if (!cancelled) {
          setSheet(null);
          setShipments([]);
          Alert.error(error.response?.data?.error || 'Failed to load ML Sheet');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [mlSheetId]);

  if (!mlSheetId) return null;

  const handleBackdrop = (event) => {
    if (event.target === event.currentTarget) onClose();
  };

  const handleDownloadPdf = async () => {
    try {
      setPdfBusy(true);
      const response = await mlSheetService.generatePDF(mlSheetId);
      const saved = await downloadBlob(response.data, `ml-sheet-${mlSheetId}.pdf`, 'application/pdf');
      if (saved?.method === 'cancelled') return;
      Alert.success('PDF downloaded successfully');
    } catch (error) {
      Alert.error(error.response?.data?.error || 'Failed to download PDF');
    } finally {
      setPdfBusy(false);
    }
  };

  const handlePrintPdf = async () => {
    try {
      setPdfBusy(true);
      const response = await mlSheetService.generatePDF(mlSheetId);
      const result = await printPdfBlob(response.data, `ml-sheet-${mlSheetId}.pdf`);
      if (result.method === 'blocked') {
        Alert.error('Could not open the print dialog');
      }
    } catch (error) {
      Alert.error(await parseBlobErrorMessage(error, 'Failed to print PDF'));
    } finally {
      setPdfBusy(false);
    }
  };

  const handleDownloadQr = async () => {
    const ids = shipments.map((s) => s.shipment_id).filter(Boolean);
    if (!ids.length) {
      Alert.error('No shipments to print QR for');
      return;
    }
    try {
      setQrBusy(true);
      await downloadShipmentQrCodesBulk(ids, `shipment_qr_codes_${mlSheetId}.pdf`);
    } finally {
      setQrBusy(false);
    }
  };

  const openShipment = (shipmentId) => {
    if (onOpenShipment && shipmentId) onOpenShipment(shipmentId);
  };

  const canView = sheet ? canViewMlSheet(sheet, user) : false;
  const canEdit = sheet ? canEditMlSheet(sheet, user) : false;
  const canAdd = sheet ? canAppendShipmentsOnMlSheet(sheet, user) : false;
  const canQr = canPrintShipmentQr(user);
  const hideAmounts = sheet ? hideMlSheetAmounts(sheet, user) : false;
  const canPdf = sheet ? canDownloadMlSheetPdf(sheet, user, shipments) : false;

  const actionItems = [];
  if (canView) {
    actionItems.push({
      key: 'view',
      icon: '👁️',
      label: t('pages.mlSheet.viewMlSheet', { defaultValue: 'View ML Sheet' }),
      onClick: () => navigate(`/ml-sheets/view/${mlSheetId}`),
    });
  }
  if (canEdit) {
    actionItems.push({
      key: 'edit',
      icon: '✏️',
      label: t('common.edit', { defaultValue: 'Edit' }),
      onClick: () => navigate(`/ml-sheets/edit/${mlSheetId}`),
    });
  }
  if (canAdd) {
    actionItems.push({
      key: 'add',
      icon: '📦',
      label: t('pages.mlSheet.addShipment', { defaultValue: 'Add Shipment' }),
      onClick: () => navigate(`/ml-sheets/${mlSheetId}/add-shipment`),
    });
  }
  if (canPdf) {
    actionItems.push({
      key: 'pdf',
      icon: '⬇️',
      label: pdfBusy
        ? t('common.pleaseWait', { defaultValue: 'Please wait...' })
        : t('pages.mlSheet.downloadPdf', { defaultValue: 'Download PDF' }),
      onClick: () => { if (!pdfBusy) handleDownloadPdf(); },
    });
    actionItems.push({
      key: 'print',
      icon: '🖨️',
      label: t('pages.mlSheet.printPdf', { defaultValue: 'Print PDF' }),
      onClick: () => { if (!pdfBusy) handlePrintPdf(); },
    });
  }
  if (canQr) {
    actionItems.push({
      key: 'qr',
      icon: '🔳',
      label: qrBusy
        ? t('common.pleaseWait', { defaultValue: 'Please wait...' })
        : t('pages.mlSheet.downloadShipmentQr', { defaultValue: 'Download Shipment QR' }),
      onClick: () => { if (!qrBusy) handleDownloadQr(); },
    });
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[85] bg-black/50 flex items-center justify-center p-3 sm:p-5 overscroll-contain"
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={t('dashboard.mlSheetNo', { defaultValue: 'ML Sheet No' })}
    >
      <div className="bg-white w-full min-w-0 max-h-[calc(100dvh-1.5rem)] sm:max-h-[90vh] rounded-xl sm:w-[calc(100vw-1.5rem)] sm:max-w-[92rem] overflow-hidden flex flex-col border-2 border-blue-200 shadow-lg">
        <div className="flex items-start justify-between gap-3 px-4 sm:px-6 py-4 border-b border-blue-100">
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800">
              {t('pages.mlSheet.mlSheetId', { defaultValue: 'ML Sheet NO' })} {mlSheetId}
            </h3>
            {sheet && (
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5 truncate">
                {(sheet.vehicle_number || '-')} · {(sheet.status || '-')} · {shipments.length} shipment{shipments.length === 1 ? '' : 's'}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {sheet && actionItems.length > 0 && (
              <RowActionsMenu
                isOpen={actionsOpen}
                onToggle={() => setActionsOpen((open) => !open)}
                onClose={() => setActionsOpen(false)}
                menuRef={actionsMenuRef}
                extraItems={actionItems}
              />
            )}
            <Button variant="ghost" onClick={onClose} className="!p-1.5" aria-label={t('common.close')}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-auto p-4 sm:p-6">
          {loading ? (
            <Loader fullScreen={false} message={t('common.loading')} />
          ) : shipments.length === 0 ? (
            <p className="text-center text-gray-400 py-8">{t('common.noData')}</p>
          ) : (
            <>
              <div className="sm:hidden space-y-2.5">
                {shipments.map((shipment) => {
                  const sheetNo = shipment.ml_sheet_id || shipment.mlSheetId || shipment.ml_sheet_no || sheet?.ml_sheet_id || mlSheetId;
                  const qty = shipment.qty ?? shipment.no_of_articles;
                  const freight = shipment.freight ?? shipment.freight_charges ?? shipment.total_amount;
                  return (
                    <div
                      key={shipment.shipment_id}
                      className="w-full text-left p-4 bg-gray-50 rounded-xl border border-blue-100"
                    >
                      <button
                        type="button"
                        onClick={() => openShipment(shipment.shipment_id)}
                        className="text-sm font-normal text-blue-600 break-all hover:underline text-left"
                      >
                        {dash(shipment.shipment_id)}
                      </button>
                      <p className="text-xs mt-1">
                        <span className="text-gray-500">{t('dashboard.mlSheetNo', { defaultValue: 'ML Sheet No' })}: </span>
                        {sheetNo ? (
                          <button type="button" className="text-blue-600 hover:underline font-normal">
                            {sheetNo}
                          </button>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 mt-1.5">
                        {(shipment.from_hub_name || shipment.sender_location || shipment.from_location || '-')}
                        {' → '}
                        {(shipment.to_hub_name || shipment.receiver_location || shipment.to_location || '-')}
                      </p>
                      <p className="text-xs text-gray-600 mt-1">
                        {shipment.status || '-'} · Qty {dash(qty)}
                        {!hideAmounts ? ` · ${freight == null || freight === '' ? '-' : formatCurrency(freight)}` : ''}
                      </p>
                    </div>
                  );
                })}
              </div>
              <div className="hidden sm:block w-full min-w-0 overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full min-w-0 text-sm table-fixed border-collapse">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className={`text-left w-[4%] ${TABLE_HEAD_CELL}`}>{t('common.sNo')}</th>
                      <th className={`text-left w-[14%] ${TABLE_HEAD_CELL}`}>GC No.</th>
                      <th className={`text-left w-[13%] ${TABLE_HEAD_CELL}`}>{t('dashboard.mlSheetNo', { defaultValue: 'ML Sheet No' })}</th>
                      <th className={`text-left w-[13%] ${TABLE_HEAD_CELL}`}>{t('dashboard.bookedDate')}</th>
                      <th className={`text-left w-[12%] ${TABLE_HEAD_CELL}`}>{t('common.from')}</th>
                      <th className={`text-left w-[12%] ${TABLE_HEAD_CELL}`}>{t('common.to')}</th>
                      <th className={`text-left w-[7%] px-1 ${TABLE_HEAD_CELL}`}>{t('dashboard.paymentStatus')}</th>
                      <th className={`text-left w-[8%] px-1 ${TABLE_HEAD_CELL}`}>{t('common.status')}</th>
                      <th className={`text-left w-[6%] ${TABLE_HEAD_CELL}`}>{t('dashboard.articles', { defaultValue: 'Article' })}</th>
                      <th className={`text-left w-[6%] ${TABLE_HEAD_CELL}`}>{t('dashboard.qty', { defaultValue: 'Quantity' })}</th>
                      {!hideAmounts && (
                      <th className={`text-left w-[6%] ${TABLE_HEAD_CELL}`}>{t('dashboard.freight')}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {shipments.map((shipment, idx) => {
                      const sheetNo = shipment.ml_sheet_id || shipment.mlSheetId || shipment.ml_sheet_no || sheet?.ml_sheet_id || mlSheetId;
                      const payLabel = paymentBadgeLabel(shipment);
                      const articles = shipment.no_of_articles;
                      const qty = shipment.qty ?? shipment.no_of_articles;
                      const freight = shipment.freight ?? shipment.freight_charges ?? shipment.total_amount;
                      return (
                        <tr
                          key={shipment.shipment_id || idx}
                          className="cursor-pointer hover:bg-blue-50/50"
                          onClick={() => openShipment(shipment.shipment_id)}
                        >
                          <td className={TABLE_CELL}>{idx + 1}</td>
                          <td className={`${TABLE_CELL} break-all`}>
                            <button
                              type="button"
                              className="text-blue-600 hover:underline font-normal"
                              onClick={() => openShipment(shipment.shipment_id)}
                            >
                              {dash(shipment.shipment_id)}
                            </button>
                          </td>
                          <td className={`${TABLE_CELL} break-all`}>
                            {sheetNo ? (
                              <button
                                type="button"
                                className="text-blue-600 hover:underline font-normal"
                                onClick={(event) => event.stopPropagation()}
                              >
                                {sheetNo}
                              </button>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className={TABLE_CELL}>{formatBooked(shipment.booked_date || shipment.created_at)}</td>
                          <td className={`${TABLE_CELL} break-words`}>
                            {shipment.from_hub_name || shipment.sender_location || shipment.from_location || '-'}
                          </td>
                          <td className={`${TABLE_CELL} break-words`}>
                            {shipment.to_hub_name || shipment.receiver_location || shipment.to_location || '-'}
                          </td>
                          <td className={TABLE_CELL_COMPACT}>
                            {payLabel ? (
                              <span className="inline-block px-1.5 py-0.5 rounded text-[11px] leading-tight bg-gray-100 text-gray-700 whitespace-nowrap">
                                {payLabel}
                              </span>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className={`${TABLE_CELL_COMPACT} break-words text-[13px]`}>{shipment.status || '-'}</td>
                          <td className={TABLE_CELL}>{dash(articles)}</td>
                          <td className={TABLE_CELL}>{dash(qty)}</td>
                          {!hideAmounts && (
                          <td className={`whitespace-nowrap ${TABLE_CELL}`}>
                            {freight == null || freight === '' ? '-' : formatCurrency(freight)}
                          </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MlSheetDetailModal;
